import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowRight, BadgeCheck, Check, CircleDollarSign, ClipboardCheck, FileText,
  Lock, MessageSquarePlus, Pencil, Plus, Trash2, X,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PID = "avo-fence-2026";

const STEPS = ["Scope Review", "Scope Frozen", "Quote", "Payment"];
const RANK = { scope_review: 0, changes_requested: 0, scope_frozen: 1, quote_sent: 2, quote_accepted: 2, paid: 3 };
const STATUS_LABEL = {
  scope_review: "Awaiting your review",
  changes_requested: "Revision in progress",
  scope_frozen: "Scope frozen",
  quote_sent: "Quote issued",
  quote_accepted: "Quote accepted",
  paid: "Paid — kickoff next",
};

const Stepper = ({ status }) => (
  <div className="flex items-center gap-0">
    {STEPS.map((s, i) => {
      const done = RANK[status] > i;
      const active = RANK[status] === i;
      return (
        <div key={s} className="flex flex-1 items-center">
          <div className="flex flex-col items-center">
            <div className={`flex h-8 w-8 items-center justify-center rounded-full border-2 font-mono text-[11px] font-bold transition-colors ${
              done ? "border-[#2E5B3E] bg-[#2E5B3E] text-white" :
              active ? "border-[#F4B032] bg-[#F4B032] text-[#0F0E0C]" :
              "border-[#E5E2D9] bg-white text-[#A59E96]"
            }`}>
              {done ? <Check className="h-4 w-4" /> : i + 1}
            </div>
            <span className={`mt-2 whitespace-nowrap font-mono text-[9px] uppercase tracking-[0.15em] ${active || done ? "text-[#1C1B1A]" : "text-[#A59E96]"}`}>{s}</span>
          </div>
          {i < STEPS.length - 1 && <div className={`mx-2 mb-5 h-0.5 flex-1 ${RANK[status] > i ? "bg-[#2E5B3E]" : "bg-[#E5E2D9]"}`} />}
        </div>
      );
    })}
  </div>
);

const Modal = ({ title, onClose, children, testId }) => (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
    <motion.div
      data-testid={testId}
      initial={{ y: 30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      className="w-full max-w-md border border-[#E5E2D9] bg-white p-6 shadow-2xl"
    >
      <div className="flex items-center justify-between">
        <h3 className="font-condensed text-2xl font-bold uppercase text-[#1C1B1A]">{title}</h3>
        <button onClick={onClose} className="text-[#A59E96] transition-colors hover:text-[#1C1B1A]"><X className="h-5 w-5" /></button>
      </div>
      <div className="mt-5">{children}</div>
    </motion.div>
  </motion.div>
);

const Proposal = ({ sessionId }) => {
  const [doc, setDoc] = useState(null);
  const [err, setErr] = useState(null);
  const [modal, setModal] = useState(null);
  const [name, setName] = useState("");
  const [changeText, setChangeText] = useState("");
  const [busy, setBusy] = useState(false);
  const [pin, setPin] = useState(() => sessionStorage.getItem("pd_pin") || "");
  const [pinDraft, setPinDraft] = useState("");
  const [adminOn, setAdminOn] = useState(false);
  const [draftItems, setDraftItems] = useState(null);
  const [reviseNote, setReviseNote] = useState("");
  const [quoteRows, setQuoteRows] = useState([{ label: "", amount: "" }]);
  const [quoteNote, setQuoteNote] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/proposal/${PID}`);
      setDoc(r.data);
    } catch (e) {
      setErr("Could not load the proposal. Please try again.");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!sessionId) return;
    let alive = true;
    const poll = async () => {
      for (let i = 0; i < 10 && alive; i++) {
        try {
          const r = await axios.get(`${API}/proposal/${PID}/payment-status/${sessionId}`);
          if (r.data.payment_status === "paid") { await load(); return; }
        } catch (e) { /* keep polling */ }
        await new Promise((res) => setTimeout(res, 2000));
      }
    };
    poll();
    return () => { alive = false; };
  }, [sessionId, load]);

  const act = async (fn) => {
    setBusy(true);
    try {
      const r = await fn();
      if (r?.data) setDoc(r.data);
      setModal(null);
      setChangeText("");
    } catch (e) {
      alert(e.response?.data?.detail || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  const pay = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/proposal/${PID}/checkout`, { origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      alert(e.response?.data?.detail || "Could not start checkout");
      setBusy(false);
    }
  };

  const adminPost = (path, body) => act(() =>
    axios.post(`${API}/proposal/${PID}${path}`, body, { headers: { "X-Admin-Pin": pin } })
  );

  if (err) return <div className="flex min-h-screen items-center justify-center bg-[#F7F6F2] font-jakarta text-[#635F59]">{err}</div>;
  if (!doc) return <div className="flex min-h-screen items-center justify-center bg-[#F7F6F2] font-mono text-xs uppercase tracking-[0.3em] text-[#A59E96]">Loading proposal…</div>;

  const scope = doc.scope;
  const quote = doc.quote;
  const isAdmin = Boolean(pin);

  return (
    <div data-testid="proposal-page" className="min-h-screen bg-[#F7F6F2] pb-44 font-jakarta text-[#1C1B1A]">
      <header className="border-b border-[#E5E2D9] bg-white px-6 py-5 md:px-12">
        <div className="mx-auto flex max-w-4xl items-center justify-between">
          <img src="/avo-logo.png" alt="AVO Fence & Supply" className="h-8 w-auto" />
          <img src="/prodigitas-logo-dark.png" alt="Prodigitas" className="h-6 w-auto" />
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-6 pt-12 md:px-12">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#8B7768]">{scope.company} · {scope.client_name}</p>
            <h1 className="mt-2 font-condensed text-4xl font-extrabold uppercase tracking-tight md:text-5xl">{scope.title}</h1>
          </div>
          <div className="flex items-center gap-2">
            <span className="rounded-full border border-[#E5E2D9] bg-white px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-[#635F59]">Scope v{doc.version}</span>
            <span data-testid="proposal-status-badge" className="rounded-full bg-[#1C1B1A] px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-[#F4B032]">{STATUS_LABEL[doc.status]}</span>
          </div>
        </div>

        <div className="mt-10 border border-[#E5E2D9] bg-white p-6 md:p-8">
          <Stepper status={doc.status} />
        </div>

        {doc.status === "changes_requested" && (
          <div data-testid="revision-banner" className="mt-6 border-l-4 border-[#F4B032] bg-[#F4B032]/10 p-5">
            <p className="text-sm text-[#635F59]"><b className="text-[#1C1B1A]">Revision in progress.</b> Prodigitas is updating the scope based on your feedback and will publish a new version here.</p>
          </div>
        )}
        {doc.status === "scope_frozen" && (
          <div data-testid="frozen-banner" className="mt-6 border-l-4 border-[#2E5B3E] bg-[#2E5B3E]/10 p-5">
            <p className="flex items-center gap-2 text-sm text-[#635F59]">
              <BadgeCheck className="h-4 w-4 text-[#2E5B3E]" />
              <span><b className="text-[#1C1B1A]">Scope frozen{doc.approved_by ? ` by ${doc.approved_by}` : ""}.</b> Prodigitas will now issue firm pricing for this exact scope.</span>
            </p>
          </div>
        )}
        {doc.status === "paid" && (
          <div data-testid="payment-success-banner" className="mt-6 border-l-4 border-[#2E5B3E] bg-[#2E5B3E]/10 p-5">
            <p className="flex items-center gap-2 text-sm text-[#635F59]">
              <CircleDollarSign className="h-4 w-4 text-[#2E5B3E]" />
              <span><b className="text-[#1C1B1A]">Payment received.</b> Thank you — Prodigitas will reach out to schedule kickoff.</span>
            </p>
          </div>
        )}

        <p className="mt-10 text-base leading-relaxed text-[#635F59]">{scope.intro}</p>

        <h2 className="mt-12 font-condensed text-3xl font-extrabold uppercase tracking-tight">Current Scope</h2>
        {[1, 2].map((phase) => {
          const phaseItems = scope.items.filter((it) => (it.phase || 1) === phase);
          if (!phaseItems.length) return null;
          return (
            <div key={phase}>
              <p className="mt-8 font-mono text-[10px] uppercase tracking-[0.3em] text-[#8B7768]">
                {phase === 1 ? "Phase 1 — Core Foundation" : "Phase 2 — Expansion"}
              </p>
              <div className="mt-4 space-y-4">
                {phaseItems.map((it) => (
                  <div key={it.title} data-testid={`scope-item-${scope.items.indexOf(it)}`} className="border border-[#E5E2D9] bg-white p-6">
                    <div className="flex items-start gap-4">
                      <span className="font-condensed text-3xl font-extrabold text-[#F4B032]">{String(scope.items.indexOf(it) + 1).padStart(2, "0")}</span>
                      <div>
                        <h3 className="font-condensed text-xl font-bold uppercase tracking-tight">{it.title}</h3>
                        <p className="mt-1 text-sm text-[#635F59]">{it.intro}</p>
                        <ul className="mt-3 space-y-1.5">
                          {it.bullets.map((b, j) => (
                            <li key={j} className="flex gap-2 text-sm text-[#635F59]">
                              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rotate-45 bg-[#F4B032]" />{b}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}

        {scope.pricing_options && (
          <>
            <h2 className="mt-12 font-condensed text-3xl font-extrabold uppercase tracking-tight">Investment Options</h2>
            <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#8B7768]">Phase 1 options — a firm quote is issued once you freeze the scope</p>
            <div className="mt-6 grid gap-4 md:grid-cols-2">
              {scope.pricing_options.map((opt, i) => (
                <div key={i} data-testid={`pricing-option-${i}`} className={`bg-white p-6 ${opt.recommended ? "border-2 border-[#1C1B1A]" : "border border-[#E5E2D9]"}`}>
                  {opt.recommended && (
                    <span className="inline-block rounded-full bg-[#F4B032] px-3 py-1 font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-[#0F0E0C]">Recommended</span>
                  )}
                  <h3 className="mt-2 font-condensed text-xl font-bold uppercase tracking-tight">{opt.name}</h3>
                  <p className="mt-1 font-condensed text-4xl font-extrabold">${opt.price.toLocaleString()}</p>
                  <ul className="mt-4 space-y-1.5">
                    {opt.includes.map((inc, j) => (
                      <li key={j} className="flex gap-2 text-sm text-[#635F59]">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#2E5B3E]" />{inc}
                      </li>
                    ))}
                  </ul>
                  {opt.excludes && <p className="mt-3 text-xs italic text-[#8B7768]">{opt.excludes}</p>}
                </div>
              ))}
            </div>
            {scope.phase2_note && <p className="mt-4 text-xs leading-relaxed text-[#635F59]">{scope.phase2_note}</p>}
          </>
        )}

        <h2 className="mt-12 font-condensed text-3xl font-extrabold uppercase tracking-tight">Suggested Future Enhancements</h2>
        <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#8B7768]">Not in current scope — priced separately when you are ready</p>
        <div className="mt-6 grid gap-4 md:grid-cols-2">
          {scope.enhancements.map((e, i) => (
            <div key={i} className="border border-dashed border-[#8B7768]/50 bg-white/60 p-6">
              <h3 className="font-condensed text-lg font-bold uppercase tracking-tight">{e.title}</h3>
              <ul className="mt-3 space-y-1.5">
                {e.bullets.map((b, j) => (
                  <li key={j} className="flex gap-2 text-sm text-[#635F59]">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rotate-45 bg-[#8B7768]" />{b}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <h2 className="mt-12 font-condensed text-3xl font-extrabold uppercase tracking-tight">How We Will Work Together</h2>
        <ul className="mt-4 space-y-2">
          {scope.working.map((w, i) => (
            <li key={i} className="flex gap-2 text-sm text-[#635F59]">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#2E5B3E]" />{w}
            </li>
          ))}
        </ul>

        {quote && (
          <div data-testid="quote-card" className="mt-12 border-2 border-[#1C1B1A] bg-white">
            <div className="flex items-center justify-between border-b border-[#E5E2D9] bg-[#1C1B1A] px-6 py-4">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#F4B032]">Firm Quote — Scope v{doc.version}</p>
              <FileText className="h-4 w-4 text-[#F4B032]" />
            </div>
            <div className="p-6">
              {quote.items.map((q, i) => (
                <div key={i} className="flex items-baseline justify-between border-b border-[#F0EDE6] py-3 text-sm last:border-0">
                  <span className="text-[#1C1B1A]">{q.label}</span>
                  <span className="font-mono font-medium">${q.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                </div>
              ))}
              <div className="mt-4 flex items-baseline justify-between border-t-2 border-[#1C1B1A] pt-4">
                <span className="font-condensed text-xl font-bold uppercase">Total</span>
                <span data-testid="quote-total" className="font-condensed text-3xl font-extrabold">${quote.total.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
              </div>
              {quote.note && <p className="mt-3 text-xs italic text-[#635F59]">{quote.note}</p>}
            </div>
          </div>
        )}

        {doc.comments.length > 0 && (
          <div className="mt-12">
            <h2 className="font-condensed text-2xl font-extrabold uppercase tracking-tight">Activity &amp; Feedback</h2>
            <div className="mt-4 space-y-3">
              {doc.comments.map((c, i) => (
                <div key={i} className={`border p-4 text-sm ${c.role === "team" ? "border-[#E5E2D9] bg-white" : "border-[#F4B032]/50 bg-[#F4B032]/5"}`}>
                  <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#8B7768]">{c.author} · {c.role === "team" ? "Prodigitas" : scope.company} · {new Date(c.at).toLocaleString()}</p>
                  <p className="mt-1.5 text-[#1C1B1A]">{c.text}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-14 border-t border-[#E5E2D9] pt-6 text-center">
          {isAdmin ? (
            <button data-testid="admin-panel-toggle" onClick={() => setAdminOn(!adminOn)} className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#8B7768] underline underline-offset-4">
              {adminOn ? "Hide team tools" : "Prodigitas team tools"}
            </button>
          ) : (
            <button data-testid="admin-login-open" onClick={() => setModal("pin")} className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-[#A59E96] transition-colors hover:text-[#1C1B1A]">
              <Lock className="h-3 w-3" /> Prodigitas team access
            </button>
          )}
        </div>

        {isAdmin && adminOn && (
          <div data-testid="admin-tools" className="mt-6 space-y-6 border-2 border-dashed border-[#F4B032] bg-[#F4B032]/5 p-6">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#8B7768]">Team tools — visible only to Prodigitas</p>

            <div>
              <h3 className="font-condensed text-xl font-bold uppercase">Revise Scope</h3>
              <input value={reviseNote} onChange={(e) => setReviseNote(e.target.value)} placeholder="Revision note (what changed and why)" data-testid="admin-revise-note" className="mt-3 w-full border border-[#E5E2D9] bg-white px-4 py-3 text-sm focus:border-[#F4B032] focus:outline-none" />
              {!draftItems ? (
                <button data-testid="admin-revise-start" onClick={() => setDraftItems(JSON.parse(JSON.stringify(scope.items)))} className="mt-3 inline-flex items-center gap-2 rounded-full border border-[#1C1B1A] px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors hover:bg-[#1C1B1A] hover:text-white">
                  <Pencil className="h-3.5 w-3.5" /> Edit scope items
                </button>
              ) : (
                <div className="mt-4 space-y-4">
                  {draftItems.map((it, i) => (
                    <div key={i} className="space-y-2 border border-[#E5E2D9] bg-white p-4">
                      <div className="flex items-center justify-between gap-2">
                        <input value={it.title} onChange={(e) => setDraftItems(draftItems.map((x, xi) => xi === i ? { ...x, title: e.target.value } : x))} className="w-full border border-[#E5E2D9] px-3 py-2 font-condensed text-lg font-bold uppercase focus:border-[#F4B032] focus:outline-none" />
                        <select value={it.phase || 1} onChange={(e) => setDraftItems(draftItems.map((x, xi) => xi === i ? { ...x, phase: Number(e.target.value) } : x))} className="border border-[#E5E2D9] bg-white px-2 py-2 font-mono text-[10px] uppercase focus:border-[#F4B032] focus:outline-none">
                          <option value={1}>Phase 1</option>
                          <option value={2}>Phase 2</option>
                        </select>
                        <button onClick={() => setDraftItems(draftItems.filter((_, xi) => xi !== i))} className="text-[#A59E96] hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                      </div>
                      <textarea value={it.intro} rows={2} onChange={(e) => setDraftItems(draftItems.map((x, xi) => xi === i ? { ...x, intro: e.target.value } : x))} className="w-full border border-[#E5E2D9] px-3 py-2 text-sm focus:border-[#F4B032] focus:outline-none" />
                      <textarea value={it.bullets.join("\n")} rows={4} onChange={(e) => setDraftItems(draftItems.map((x, xi) => xi === i ? { ...x, bullets: e.target.value.split("\n").filter(Boolean) } : x))} className="w-full border border-[#E5E2D9] px-3 py-2 font-mono text-xs focus:border-[#F4B032] focus:outline-none" placeholder="One bullet per line" />
                    </div>
                  ))}
                  <div className="flex flex-wrap gap-3">
                    <button onClick={() => setDraftItems([...draftItems, { title: "New Item", intro: "", bullets: [] }])} className="inline-flex items-center gap-2 rounded-full border border-[#1C1B1A] px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em]"><Plus className="h-3.5 w-3.5" /> Add item</button>
                    <button data-testid="admin-revise-publish" disabled={busy || !reviseNote} onClick={() => adminPost("/admin/revise", { note: reviseNote, items: draftItems }).then(() => { setDraftItems(null); setReviseNote(""); })} className="inline-flex items-center gap-2 rounded-full bg-[#1C1B1A] px-5 py-2 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-[#F4B032] disabled:opacity-40">
                      Publish scope v{doc.version + 1} <ArrowRight className="h-3.5 w-3.5" />
                    </button>
                    <button onClick={() => setDraftItems(null)} className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#A59E96]">Cancel</button>
                  </div>
                </div>
              )}
            </div>

            {doc.status === "scope_frozen" && (
              <div className="border-t border-[#F4B032]/40 pt-6">
                <h3 className="font-condensed text-xl font-bold uppercase">Build Quote</h3>
                {quoteRows.map((r, i) => (
                  <div key={i} className="mt-3 flex gap-2">
                    <input value={r.label} onChange={(e) => setQuoteRows(quoteRows.map((x, xi) => xi === i ? { ...x, label: e.target.value } : x))} placeholder="Line item (e.g. Module 1 — Website & Catalog)" className="w-full border border-[#E5E2D9] bg-white px-3 py-2.5 text-sm focus:border-[#F4B032] focus:outline-none" />
                    <input value={r.amount} onChange={(e) => setQuoteRows(quoteRows.map((x, xi) => xi === i ? { ...x, amount: e.target.value } : x))} placeholder="$" type="number" min="0" data-testid={i === 0 ? "admin-quote-amount-0" : undefined} className="w-32 border border-[#E5E2D9] bg-white px-3 py-2.5 font-mono text-sm focus:border-[#F4B032] focus:outline-none" />
                    <button onClick={() => setQuoteRows(quoteRows.filter((_, xi) => xi !== i))} className="text-[#A59E96] hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
                <button onClick={() => setQuoteRows([...quoteRows, { label: "", amount: "" }])} className="mt-3 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#635F59]"><Plus className="h-3.5 w-3.5" /> Add line</button>
                <p className="mt-3 font-condensed text-2xl font-extrabold">Total: ${quoteRows.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0).toLocaleString()}</p>
                <input value={quoteNote} onChange={(e) => setQuoteNote(e.target.value)} placeholder="Note to client (payment terms, validity…)" className="mt-3 w-full border border-[#E5E2D9] bg-white px-4 py-3 text-sm focus:border-[#F4B032] focus:outline-none" />
                <button
                  data-testid="admin-quote-send"
                  disabled={busy || !quoteRows.every((r) => r.label && parseFloat(r.amount) > 0)}
                  onClick={() => adminPost("/admin/quote", { items: quoteRows.map((r) => ({ label: r.label, amount: parseFloat(r.amount) })), note: quoteNote })}
                  className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#F4B032] px-6 py-3 font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-[#0F0E0C] transition-colors hover:bg-[#FFC14D] disabled:opacity-40"
                >
                  Send Quote to Client <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        )}
      </main>

      <div className="fixed inset-x-0 bottom-20 z-40 flex justify-center px-4">
        <div className="flex flex-wrap items-center justify-center gap-3 rounded-2xl border border-[#E5E2D9] bg-white/90 px-5 py-4 shadow-2xl backdrop-blur-xl">
          {doc.status === "scope_review" && (
            <>
              <button data-testid="scope-request-changes-button" onClick={() => setModal("changes")} className="inline-flex items-center gap-2 rounded-full border border-[#1C1B1A] px-6 py-3 font-mono text-[11px] uppercase tracking-[0.2em] transition-colors hover:bg-[#1C1B1A] hover:text-white">
                <MessageSquarePlus className="h-4 w-4" /> Request Changes
              </button>
              <button data-testid="scope-approve-button" onClick={() => setModal("approve")} className="inline-flex items-center gap-2 rounded-full bg-[#F4B032] px-6 py-3 font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-[#0F0E0C] transition-colors hover:bg-[#FFC14D]">
                <ClipboardCheck className="h-4 w-4" /> Approve &amp; Freeze Scope
              </button>
            </>
          )}
          {doc.status === "changes_requested" && <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#8B7768]">Revision in progress — no action needed from you</p>}
          {doc.status === "scope_frozen" && <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#8B7768]">Scope frozen — Prodigitas is preparing your quote</p>}
          {doc.status === "quote_sent" && (
            <button data-testid="quote-accept-button" onClick={() => setModal("accept")} className="inline-flex items-center gap-2 rounded-full bg-[#F4B032] px-6 py-3 font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-[#0F0E0C] transition-colors hover:bg-[#FFC14D]">
              <BadgeCheck className="h-4 w-4" /> Accept Quote
            </button>
          )}
          {doc.status === "quote_accepted" && (
            <button data-testid="pay-with-stripe-button" disabled={busy} onClick={pay} className="inline-flex items-center gap-2 rounded-full bg-[#1C1B1A] px-6 py-3 font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-[#F4B032] transition-colors hover:bg-black disabled:opacity-50">
              <CircleDollarSign className="h-4 w-4" /> {busy ? "Opening Stripe…" : `Pay $${quote?.total.toLocaleString()} securely with Stripe`}
            </button>
          )}
          {doc.status === "paid" && <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#2E5B3E]">Payment complete — see you at kickoff</p>}
        </div>
      </div>

      <AnimatePresence>
        {modal === "changes" && (
          <Modal title="Request Changes" testId="changes-modal" onClose={() => setModal(null)}>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" data-testid="changes-name-input" className="w-full border border-[#E5E2D9] px-4 py-3 text-sm focus:border-[#F4B032] focus:outline-none" />
            <textarea value={changeText} onChange={(e) => setChangeText(e.target.value)} rows={4} placeholder="What would you like adjusted in the scope?" data-testid="changes-text-input" className="mt-3 w-full border border-[#E5E2D9] px-4 py-3 text-sm focus:border-[#F4B032] focus:outline-none" />
            <button disabled={busy || !name || !changeText} onClick={() => act(() => axios.post(`${API}/proposal/${PID}/request-changes`, { name, text: changeText }))} data-testid="changes-submit-button" className="mt-4 w-full rounded-full bg-[#1C1B1A] py-3.5 font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-[#F4B032] disabled:opacity-40">Send to Prodigitas</button>
          </Modal>
        )}
        {modal === "approve" && (
          <Modal title="Approve & Freeze Scope" testId="approve-modal" onClose={() => setModal(null)}>
            <p className="text-sm text-[#635F59]">This locks scope v{doc.version}. Prodigitas will issue firm pricing against exactly this scope.</p>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name (signature)" data-testid="approve-name-input" className="mt-4 w-full border border-[#E5E2D9] px-4 py-3 text-sm focus:border-[#F4B032] focus:outline-none" />
            <button disabled={busy || !name} onClick={() => act(() => axios.post(`${API}/proposal/${PID}/approve-scope`, { name }))} data-testid="approve-submit-button" className="mt-4 w-full rounded-full bg-[#F4B032] py-3.5 font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-[#0F0E0C] disabled:opacity-40">Freeze Scope v{doc.version}</button>
          </Modal>
        )}
        {modal === "accept" && (
          <Modal title="Accept Quote" testId="accept-modal" onClose={() => setModal(null)}>
            <p className="text-sm text-[#635F59]">You are accepting the quote of <b>${quote?.total.toLocaleString()}</b> for frozen scope v{doc.version}. You will then be able to pay securely by card.</p>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name (signature)" data-testid="accept-name-input" className="mt-4 w-full border border-[#E5E2D9] px-4 py-3 text-sm focus:border-[#F4B032] focus:outline-none" />
            <button disabled={busy || !name} onClick={() => act(() => axios.post(`${API}/proposal/${PID}/accept-quote`, { name }))} data-testid="accept-submit-button" className="mt-4 w-full rounded-full bg-[#F4B032] py-3.5 font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-[#0F0E0C] disabled:opacity-40">Accept Quote</button>
          </Modal>
        )}
        {modal === "pin" && (
          <Modal title="Team Access" testId="pin-modal" onClose={() => setModal(null)}>
            <input value={pinDraft} onChange={(e) => setPinDraft(e.target.value)} placeholder="Team PIN" type="password" data-testid="admin-pin-input" className="w-full border border-[#E5E2D9] px-4 py-3 text-sm focus:border-[#F4B032] focus:outline-none" />
            <button
              data-testid="admin-pin-submit"
              onClick={() => { sessionStorage.setItem("pd_pin", pinDraft); setPin(pinDraft); setModal(null); setAdminOn(true); }}
              className="mt-4 w-full rounded-full bg-[#1C1B1A] py-3.5 font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-[#F4B032]"
            >
              Unlock Team Tools
            </button>
          </Modal>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Proposal;
